import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
  type ChangeEvent,
} from "react";
import clsx from "clsx";

import { Select } from "../select/select.tsx";
import { TextField } from "../text-field/text-field.tsx";

import styles from "./time-period-select.module.css";
import {
  amountForUnitLabels,
  clampTimePeriodAmount,
  digitsOnlyAmountInput,
  getTimePeriodUnitOptions,
  type ClampTimePeriodOptions,
  type TimePeriodUnit,
  type TimePeriodValue,
} from "./time-period-utils.ts";

export interface TimePeriodSelectProps extends ClampTimePeriodOptions {
  id?: string;
  value: TimePeriodValue;
  onChange: (value: TimePeriodValue) => void;
  disabled?: boolean;
  error?: string;
}

export function TimePeriodSelect({
  id: idProp,
  value,
  onChange,
  disabled = false,
  error,
  minAmount = 1,
  maxDays = 365,
  maxMonths = 12,
}: TimePeriodSelectProps) {
  const reactId = useId().replace(/:/g, "");
  const rootId = idProp ?? `time-period-${reactId}`;
  const amountId = `${rootId}-amount`;
  const unitId = `${rootId}-unit`;
  const errorId = `${rootId}-error`;

  const [amountText, setAmountText] = useState(() =>
    value.amount > 0 ? String(value.amount) : "",
  );

  const clampOptions = useMemo(
    () => ({ minAmount, maxDays, maxMonths }),
    [minAmount, maxDays, maxMonths],
  );

  useEffect(() => {
    if (value.amount > 0) {
      setAmountText(String(value.amount));
    }
  }, [value.amount, value.unit]);

  const labelAmount = useMemo(
    () => amountForUnitLabels(amountText, minAmount),
    [amountText, minAmount],
  );

  const unitOptions = useMemo(
    () => getTimePeriodUnitOptions(labelAmount),
    [labelAmount],
  );

  const hasError = error != null && error !== "";

  const commitAmount = useCallback(
    (digits: string) => {
      if (digits === "") {
        setAmountText("");
        onChange({ ...value, amount: 0 });
        return;
      }

      const parsed = parseInt(digits, 10);
      const clamped = clampTimePeriodAmount(parsed, value.unit, clampOptions);
      setAmountText(String(clamped));
      onChange({ ...value, amount: clamped });
    },
    [clampOptions, onChange, value],
  );

  const handleAmountChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const digits = digitsOnlyAmountInput(event.target.value);
      commitAmount(digits);
    },
    [commitAmount],
  );

  const handleAmountBlur = useCallback(() => {
    if (amountText === "" || value.amount < minAmount) {
      const clamped = clampTimePeriodAmount(
        value.amount > 0 ? value.amount : minAmount,
        value.unit,
        clampOptions,
      );
      setAmountText(String(clamped));
      onChange({ ...value, amount: clamped });
    }
  }, [amountText, clampOptions, minAmount, onChange, value]);

  const handleUnitChange = useCallback(
    (nextUnit: string) => {
      const unit = nextUnit as TimePeriodUnit;
      const digits = amountText === "" ? "" : digitsOnlyAmountInput(amountText);
      const parsed =
        digits === "" ? minAmount : parseInt(digits, 10);
      const clamped = clampTimePeriodAmount(parsed, unit, clampOptions);
      setAmountText(String(clamped));
      onChange({ amount: clamped, unit });
    },
    [amountText, clampOptions, minAmount, onChange],
  );

  return (
    <div className={styles.root}>
      <div className={styles.row}>
        <div className={styles.amountField}>
          <TextField
            id={amountId}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            maxLength={3}
            value={amountText}
            disabled={disabled}
            error={error}
            showErrorMessage={false}
            aria-describedby={hasError ? errorId : undefined}
            onChange={handleAmountChange}
            onBlur={handleAmountBlur}
          />
        </div>
        <div className={styles.unitSelect}>
          <Select
            id={unitId}
            value={value.unit}
            options={unitOptions}
            disabled={disabled}
            onChange={handleUnitChange}
          />
        </div>
      </div>
      {hasError ? (
        <p id={errorId} className={clsx(styles.message)} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
